import { useState, useEffect, useRef } from "react";
import { Wordmark } from "@/components/wordmark";
import ReportWidget from "@/components/report-widget";
import { useLocation, Link } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useWorkspaceStore } from "@/hooks/use-workspace";
import { useStudioHref } from "@/hooks/use-studio-href";
import { useAuth } from "@/hooks/use-auth";
import { useContent } from "@/hooks/use-content";
import { useToast } from "@/hooks/use-toast";
import {
  LayoutDashboard, Compass, PenTool, Target, MessageSquare, Settings,
  ChevronDown, ChevronLeft, ChevronRight, Plus, Building2, LogOut, Radar,
  HelpCircle, Send, X, Loader2, Moon, Sun, SunMoon, Users, Mail, Rocket, Menu,
  FlaskConical, RotateCcw, AlertTriangle, Sparkles, CreditCard, BookOpen, Trash2,
  ClipboardList, Eye, Monitor
} from "lucide-react";
import { ToastAction } from "@/components/ui/toast";
import { useTheme } from "@/components/theme-provider";
import Onboarding from "@/pages/onboarding";

interface Workspace {
  id: number;
  name: string;
  url: string;
  industry: string;
  targetAudience: string;
  brandVoice: string;
}

function WorkspaceSwitcher({ onRequestOnboarding }: { onRequestOnboarding: () => void }) {
  const [isOpen, setIsOpen] = useState(false);
  const [, setLocation] = useLocation();
  const { activeWorkspaceId, setActiveWorkspaceId } = useWorkspaceStore();
  const { toast } = useToast();
  const [restoringId, setRestoringId] = useState<number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const { data: workspaces = [] } = useQuery<Workspace[]>({
    queryKey: ["/api/workspaces"],
  });

  const { data: deletedWorkspaces = [] } = useQuery<(Workspace & { deletedAt: string })[]>({
    queryKey: ["/api/workspaces-deleted"],
    queryFn: async () => {
      const res = await fetch("/api/workspaces-deleted", { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: isOpen,
    staleTime: 30000,
  });

  useEffect(() => {
    if (workspaces.length === 0) return;
    // Auto-select: if no active workspace, OR if the stored ID no longer exists in the list
    const exists = workspaces.some((w) => w.id === activeWorkspaceId);
    if (!activeWorkspaceId || !exists) {
      setActiveWorkspaceId(workspaces[0].id);
    }
  }, [workspaces, activeWorkspaceId, setActiveWorkspaceId]);

  const activeWs = workspaces.find((w) => w.id === activeWorkspaceId) || workspaces[0];

  async function handleRestore(wsId: number, wsName: string) {
    setRestoringId(wsId);
    try {
      const res = await fetch(`/api/workspaces/${wsId}/restore`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json();
        toast({ title: "Restore failed", description: err.message || "Could not restore workspace", variant: "destructive" });
        return;
      }
      toast({ title: "Workspace restored", description: `"${wsName}" is back!` });
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces"] });
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces-deleted"] });
      setActiveWorkspaceId(wsId);
      setIsOpen(false);
      setLocation("/");
    } catch {
      toast({ title: "Error", description: "Failed to restore workspace", variant: "destructive" });
    } finally {
      setRestoringId(null);
    }
  }

  async function handlePermanentDelete(wsId: number, wsName: string) {
    setDeletingId(wsId);
    try {
      const res = await fetch(`/api/workspaces/${wsId}/permanent`, {
        method: "DELETE",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ confirmName: wsName }),
      });
      if (!res.ok) {
        const err = await res.json();
        toast({ title: "Delete failed", description: err.message || "Could not permanently delete workspace", variant: "destructive" });
        return;
      }
      toast({ title: "Workspace deleted", description: `"${wsName}" has been permanently removed.` });
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces-deleted"] });
      setConfirmDeleteId(null);
    } catch {
      toast({ title: "Error", description: "Failed to permanently delete workspace", variant: "destructive" });
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div className="relative">
      <button
        data-testid="button-workspace-switcher"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-3 px-4 py-2.5 rounded-xl bg-muted/50 border border-border hover:bg-muted hover:border-border transition-all duration-300 text-sm font-semibold group"
      >
        <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500/20 to-blue-600/10 border border-blue-500/25 flex items-center justify-center">
          <Building2 size={14} className="text-blue-400" />
        </div>
        <span className="max-w-[100px] md:max-w-[160px] truncate text-foreground/80 group-hover:text-foreground transition-colors" data-testid="text-active-workspace">
          {activeWs?.name || "Select workspace"}
        </span>
        <ChevronDown size={14} className={`text-muted-foreground/60 transition-transform duration-300 ${isOpen ? "rotate-180 text-muted-foreground" : ""}`} />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setIsOpen(false)} />
          <div className="absolute top-full left-0 mt-3 w-72 bg-card/95 backdrop-blur-xl border border-border rounded-2xl shadow-[0_24px_64px_rgba(0,0,0,0.6),0_0_0_1px_rgba(255,255,255,0.03)] z-[70] p-2 animate-fade-in">
            <div className="px-3 py-2.5 border-b border-border mb-2">
              <p className="text-[10px] font-bold text-muted-foreground/60 uppercase tracking-[0.2em]">Active Tenancies</p>
            </div>
            <div className="max-h-64 overflow-y-auto custom-scrollbar px-1">
              {workspaces.map((ws) => (
                <button
                  key={ws.id}
                  data-testid={`button-workspace-${ws.id}`}
                  onClick={() => { setActiveWorkspaceId(ws.id); setIsOpen(false); }}
                  className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm transition-all duration-200 mb-1 group
                    ${ws.id === activeWorkspaceId ? "bg-blue-500/[0.08] text-blue-400 border border-blue-500/20" : "text-muted-foreground hover:bg-muted/50 hover:text-foreground/70"}
                  `}
                >
                  <div className={`w-2 h-2 rounded-full transition-all ${ws.id === activeWorkspaceId ? "bg-blue-400 shadow-[0_0_8px_rgba(59,130,246,0.5)]" : "bg-muted"}`} />
                  <span className="flex-1 text-left truncate font-medium">{ws.name}</span>
                  {ws.id === activeWorkspaceId && <div className="text-[10px] font-bold text-blue-400/50 uppercase tracking-wider">Active</div>}
                </button>
              ))}
            </div>

            {deletedWorkspaces.length > 0 && (
              <>
                <div className="h-px bg-gradient-to-r from-transparent via-foreground/[0.06] to-transparent my-2 mx-2" />
                <div className="px-3 py-1.5">
                  <p className="text-[10px] font-bold text-amber-400/70 uppercase tracking-[0.2em]">Recently Deleted</p>
                </div>
                <div className="px-1 max-h-48 overflow-y-auto">
                  {deletedWorkspaces.map((ws) => {
                    const daysAgo = Math.floor((Date.now() - new Date(ws.deletedAt).getTime()) / (1000 * 60 * 60 * 24));
                    const isConfirming = confirmDeleteId === ws.id;
                    return (
                      <div
                        key={ws.id}
                        data-testid={`deleted-workspace-${ws.id}`}
                        className="w-full flex flex-col px-3 py-2.5 rounded-xl text-sm mb-1 bg-amber-500/[0.04] border border-amber-500/10"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-2 h-2 rounded-full bg-amber-500/40 shrink-0" />
                          <div className="flex-1 min-w-0 text-left">
                            <p className="text-muted-foreground/70 font-medium truncate">{ws.name}</p>
                            <p className="text-[10px] text-muted-foreground/40">Deleted {daysAgo === 0 ? "today" : `${daysAgo}d ago`} · {30 - daysAgo}d left to restore</p>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              data-testid={`button-restore-workspace-${ws.id}`}
                              onClick={() => handleRestore(ws.id, ws.name)}
                              disabled={restoringId === ws.id || deletingId === ws.id}
                              className="flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-bold text-amber-400 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/20 transition-all disabled:opacity-50"
                            >
                              {restoringId === ws.id ? <Loader2 size={11} className="animate-spin" /> : <RotateCcw size={11} />}
                              Restore
                            </button>
                            <button
                              data-testid={`button-permanent-delete-workspace-${ws.id}`}
                              onClick={() => setConfirmDeleteId(isConfirming ? null : ws.id)}
                              disabled={restoringId === ws.id || deletingId === ws.id}
                              title="Delete Permanently"
                              className="flex items-center justify-center w-6 h-6 rounded-lg text-rose-400/60 hover:text-rose-400 hover:bg-rose-500/10 border border-transparent hover:border-rose-500/20 transition-all disabled:opacity-50"
                            >
                              <Trash2 size={11} />
                            </button>
                          </div>
                        </div>
                        {isConfirming && (
                          <div className="mt-2 ml-5 flex items-center gap-2" data-testid={`confirm-permanent-delete-${ws.id}`}>
                            <p className="text-[10px] text-rose-400/80 flex-1">Permanently delete "{ws.name}"? This cannot be undone.</p>
                            <button
                              data-testid={`button-confirm-permanent-delete-${ws.id}`}
                              onClick={() => handlePermanentDelete(ws.id, ws.name)}
                              disabled={deletingId === ws.id}
                              className="flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-bold text-rose-400 bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/20 transition-all shrink-0 disabled:opacity-50"
                            >
                              {deletingId === ws.id ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />}
                              Delete
                            </button>
                            <button
                              data-testid={`button-cancel-permanent-delete-${ws.id}`}
                              onClick={() => setConfirmDeleteId(null)}
                              className="flex items-center px-2 py-1 rounded-lg text-[11px] font-bold text-muted-foreground/60 hover:text-muted-foreground hover:bg-muted/50 transition-all shrink-0"
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            <div className="h-px bg-gradient-to-r from-transparent via-foreground/[0.06] to-transparent my-2 mx-2" />
            <button
              data-testid="button-add-workspace"
              onClick={() => { setIsOpen(false); onRequestOnboarding(); }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-blue-400 hover:bg-blue-500/[0.08] transition-all duration-200 font-bold group border border-dashed border-blue-500/20 hover:border-blue-500/30 mt-1"
            >
              <div className="w-6 h-6 rounded-md bg-blue-600/20 flex items-center justify-center">
                <Plus size={14} />
              </div>
              <span>New Workspace</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}

const primaryNavItems = [
  { to: "/", icon: Sparkles, label: "Command", agent: "Draper · All Agents" },
];

const agentNavItems = [
  { to: "/overview", icon: LayoutDashboard, label: "Overview", agent: "Command Center" },
  { to: "/strategy", icon: Compass, label: "Strategy", agent: "Strategy Hub" },
  { to: "/content", icon: PenTool, label: "Peggy", agent: "Peggy" },
  { to: "/ads", icon: Target, label: "Pete", agent: "Pete" },
  { to: "/leads", icon: MessageSquare, label: "Rogers", agent: "Rogers" },
  { to: "/harry", icon: Radar, label: "Harry", agent: "Scout Intelligence" },
  { to: "/claw", icon: Monitor, label: "Claw", agent: "Computer Use" },
];

const navItems = [...primaryNavItems, ...agentNavItems];

function HelpTicketDialog({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("medium");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { toast } = useToast();

  async function handleSubmit() {
    if (!subject.trim() || !description.trim()) return;
    setSubmitting(true);
    try {
      const res = await apiRequest("POST", "/api/support/tickets", { subject, description, priority });
      if (res.ok) {
        setSubmitted(true);
        setTimeout(() => {
          setSubmitted(false);
          setSubject("");
          setDescription("");
          setPriority("medium");
          onClose();
        }, 2000);
      }
    } catch {
      toast({ title: "Failed to submit ticket", description: "Please try again later.", variant: "destructive" });
    } finally { setSubmitting(false); }
  }

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-background/60 backdrop-blur-sm z-[100]" onClick={onClose} />
      <div className="fixed inset-0 z-[101] flex items-center justify-center p-4" data-testid="dialog-help-ticket">
        <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-5">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-display font-bold text-foreground">Submit a Support Ticket</h3>
              <p className="text-xs text-muted-foreground/70 mt-0.5">We'll get back to you as soon as possible.</p>
            </div>
            <button onClick={onClose} className="text-muted-foreground/50 hover:text-muted-foreground transition-colors" data-testid="button-close-help">
              <X size={18} />
            </button>
          </div>

          {submitted ? (
            <div className="text-center py-8" data-testid="text-ticket-submitted">
              <div className="w-12 h-12 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto mb-3">
                <HelpCircle size={20} className="text-emerald-400" />
              </div>
              <p className="text-sm font-bold text-foreground">Ticket Submitted!</p>
              <p className="text-xs text-muted-foreground/70 mt-1">Our team will review your request shortly.</p>
            </div>
          ) : (
            <>
              <div>
                <label className="text-[10px] font-bold text-muted-foreground/70 uppercase tracking-wider block mb-1.5">Subject</label>
                <input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Brief summary of your issue..."
                  className="w-full px-3 py-2.5 bg-muted/50 border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-blue-500/40"
                  data-testid="input-ticket-subject"
                />
              </div>
              <div>
                <label className="text-[10px] font-bold text-muted-foreground/70 uppercase tracking-wider block mb-1.5">Priority</label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  className="w-full px-3 py-2.5 bg-muted/50 border border-border rounded-xl text-sm text-foreground focus:outline-none focus:border-blue-500/40"
                  data-testid="select-ticket-priority"
                >
                  <option value="low" className="bg-card">Low</option>
                  <option value="medium" className="bg-card">Medium</option>
                  <option value="high" className="bg-card">High</option>
                  <option value="urgent" className="bg-card">Urgent</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] font-bold text-muted-foreground/70 uppercase tracking-wider block mb-1.5">Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe your issue in detail..."
                  rows={4}
                  className="w-full px-3 py-2.5 bg-muted/50 border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-blue-500/40 resize-none"
                  data-testid="input-ticket-description"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleSubmit}
                  disabled={submitting || !subject.trim() || !description.trim()}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-xl text-sm font-bold transition-all"
                  data-testid="button-submit-ticket"
                >
                  {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  Submit Ticket
                </button>
                <button
                  onClick={onClose}
                  className="px-4 py-2.5 text-muted-foreground hover:text-muted-foreground rounded-xl text-sm font-medium transition-all"
                  data-testid="button-cancel-ticket"
                >
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

const FEATURE_AREAS = [
  "Draper", "Peggy", "Pete", "Rogers", "Lead Desk",
  "Meta Ads / Pete Campaigns", "Social Publishing", "Funnel Pages",
  "Blog", "Settings", "General",
];

const FEEDBACK_TYPES = ["Bug", "Suggestion", "Improvement", "Question"] as const;
const SEVERITY_LEVELS = ["Low", "Medium", "High", "Critical"] as const;

function TesterFeedbackDialog({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [featureArea, setFeatureArea] = useState(FEATURE_AREAS[0]);
  const [feedbackType, setFeedbackType] = useState<typeof FEEDBACK_TYPES[number]>("Bug");
  const [severity, setSeverity] = useState<typeof SEVERITY_LEVELS[number]>("Medium");
  const [whatTested, setWhatTested] = useState("");
  const [comments, setComments] = useState("");
  const [yourName, setYourName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  function reset() {
    setFeatureArea(FEATURE_AREAS[0]);
    setFeedbackType("Bug");
    setSeverity("Medium");
    setWhatTested("");
    setComments("");
    setYourName("");
  }

  async function handleSubmit() {
    if (!comments.trim()) return;
    setSubmitting(true);
    try {
      const truncatedComment = comments.trim().slice(0, 60);
      const subject = `[Tester: ${featureArea}] ${feedbackType} — ${truncatedComment}${comments.trim().length > 60 ? "…" : ""}`;
      const severityLine = feedbackType === "Bug" ? `Severity: ${severity}\n` : "";
      const namedLine = yourName.trim() ? `Submitted by: ${yourName.trim()}\n` : "";
      const testedLine = whatTested.trim() ? `What was tested: ${whatTested.trim()}\n\n` : "";
      const description = `${namedLine}${severityLine}${testedLine}${comments.trim()}`;
      const priorityMap: Record<string, string> = { Low: "low", Medium: "medium", High: "high", Critical: "urgent" };
      const priority = feedbackType === "Bug" ? priorityMap[severity] : "medium";
      const res = await apiRequest("POST", "/api/support/tickets", {
        subject,
        description,
        priority,
        category: "tester_feedback",
      });
      if (res.ok) {
        toast({ title: "Feedback submitted!", description: "Your tester feedback has been recorded. Thanks!" });
        reset();
        onClose();
      } else {
        toast({ title: "Submission failed", description: "Please try again.", variant: "destructive" });
      }
    } catch {
      toast({ title: "Submission failed", description: "Please try again.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  }

  if (!isOpen) return null;

  const inputCls = "w-full px-3 py-2.5 bg-muted/50 border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-blue-500/40";
  const labelCls = "text-[10px] font-bold text-muted-foreground/70 uppercase tracking-wider block mb-1.5";

  return (
    <>
      <div className="fixed inset-0 bg-background/60 backdrop-blur-sm z-[100]" onClick={onClose} />
      <div className="fixed inset-0 z-[101] flex items-center justify-center p-4" data-testid="dialog-tester-feedback">
        <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4 max-h-[90vh] overflow-y-auto custom-scrollbar">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-display font-bold text-foreground">Tester Feedback</h3>
              <p className="text-xs text-muted-foreground/70 mt-0.5">Log structured feedback about a specific feature.</p>
            </div>
            <button onClick={onClose} className="text-muted-foreground/50 hover:text-muted-foreground transition-colors shrink-0" data-testid="button-close-tester-feedback">
              <X size={18} />
            </button>
          </div>

          <div>
            <label className={labelCls}>Feature Area</label>
            <select
              value={featureArea}
              onChange={(e) => setFeatureArea(e.target.value)}
              className={inputCls}
              data-testid="select-feature-area"
            >
              {FEATURE_AREAS.map((area) => (
                <option key={area} value={area} className="bg-card">{area}</option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelCls}>Type</label>
            <div className="flex gap-2" data-testid="feedback-type-selector">
              {FEEDBACK_TYPES.map((type) => (
                <button
                  key={type}
                  onClick={() => setFeedbackType(type)}
                  data-testid={`feedback-type-${type.toLowerCase()}`}
                  className={`flex-1 py-1.5 rounded-lg text-[11px] font-bold transition-all border ${
                    feedbackType === type
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-muted/40 text-muted-foreground border-border hover:border-blue-500/40"
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          {feedbackType === "Bug" && (
            <div>
              <label className={labelCls}>Severity</label>
              <div className="flex gap-2" data-testid="severity-selector">
                {SEVERITY_LEVELS.map((lvl) => {
                  const colors: Record<string, string> = {
                    Low: "bg-emerald-600 text-white border-emerald-600",
                    Medium: "bg-amber-500 text-white border-amber-500",
                    High: "bg-orange-600 text-white border-orange-600",
                    Critical: "bg-red-600 text-white border-red-600",
                  };
                  return (
                    <button
                      key={lvl}
                      onClick={() => setSeverity(lvl)}
                      data-testid={`severity-${lvl.toLowerCase()}`}
                      className={`flex-1 py-1.5 rounded-lg text-[11px] font-bold transition-all border ${
                        severity === lvl
                          ? colors[lvl]
                          : "bg-muted/40 text-muted-foreground border-border hover:border-blue-500/40"
                      }`}
                    >
                      {lvl}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <label className={labelCls}>What did you test? <span className="normal-case text-muted-foreground/40 font-normal">(optional)</span></label>
            <input
              value={whatTested}
              onChange={(e) => setWhatTested(e.target.value)}
              placeholder="e.g. Ran a nurture sequence from lead import to second email"
              className={inputCls}
              data-testid="input-what-tested"
            />
          </div>

          <div>
            <label className={labelCls}>Comments / Findings <span className="text-red-400">*</span></label>
            <textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Describe what you found, what happened, or your suggestion..."
              rows={4}
              className={`${inputCls} resize-none`}
              data-testid="textarea-comments"
            />
          </div>

          <div>
            <label className={labelCls}>Your name <span className="normal-case text-muted-foreground/40 font-normal">(optional — leave blank for anonymous)</span></label>
            <input
              value={yourName}
              onChange={(e) => setYourName(e.target.value)}
              placeholder="Anonymous"
              className={inputCls}
              data-testid="input-tester-name"
            />
          </div>

          <div className="flex gap-2 pt-1">
            <button
              onClick={handleSubmit}
              disabled={submitting || !comments.trim()}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-xl text-sm font-bold transition-all"
              data-testid="button-submit-tester-feedback"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Submit Feedback
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2.5 text-muted-foreground hover:text-muted-foreground rounded-xl text-sm font-medium transition-all"
              data-testid="button-cancel-tester-feedback"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

const themeOptions = [
  { value: "midnight" as const, icon: Moon, label: "Midnight" },
  { value: "twilight" as const, icon: SunMoon, label: "Twilight" },
  { value: "daylight" as const, icon: Sun, label: "Daylight" },
];

function LaunchStrategyCta({ wsId }: { wsId: number | null }) {
  const [location] = useLocation();
  const { data: planStatus } = useQuery<{ plan: any | null }>({
    queryKey: ["/api/workspaces", wsId, "deep-plan-status"],
    enabled: !!wsId,
    queryFn: async () => {
      const res = await fetch(`/api/workspaces/${wsId}/deep-plan/status`, { credentials: "include" });
      if (!res.ok) return { plan: null };
      return res.json();
    },
    refetchInterval: 30000,
  });
  const plan = planStatus?.plan;
  const isLocked = plan?.wizardStep === "locked" || plan?.lockedAt;
  if (isLocked || location === "/launch") return null;
  return (
    <Link
      href="/launch"
      data-testid="nav-launch-strategy"
      className="mx-2 mt-3 flex items-center gap-2 px-3 py-2.5 rounded-xl bg-gradient-to-r from-blue-600/20 to-blue-500/10 border border-blue-500/30 hover:border-blue-400/50 hover:from-blue-600/30 transition-all group"
      title="Advanced multi-step guided session for detailed strategic planning — not required for basic use"
    >
      <Rocket size={14} className="text-blue-400 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-bold text-blue-300 leading-tight">Launch Strategy Wizard</p>
        <p className="text-[9px] text-blue-400/60 leading-tight truncate">{plan ? "Continue wizard" : "Advanced guided strategy session"}</p>
      </div>
    </Link>
  );
}

function DevAiToggle({ wsId, wsName, collapsed }: { wsId: number | null; wsName: string; collapsed: boolean }) {
  const { toast } = useToast();

  const { data, isLoading } = useQuery<{ devAiEnabled: boolean; isDevEnv: boolean }>({
    queryKey: ["/api/workspaces", wsId, "dev-ai-status"],
    queryFn: async () => {
      if (!wsId) return { devAiEnabled: false, isDevEnv: false };
      const res = await fetch(`/api/workspaces/${wsId}/dev-ai-status`, { credentials: "include" });
      if (!res.ok) return { devAiEnabled: false, isDevEnv: false };
      return res.json();
    },
    enabled: !!wsId,
    refetchInterval: 10000,
  });

  const toggle = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/workspaces/${wsId}/dev-ai-toggle`, {});
      return res.json();
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", wsId, "dev-ai-status"] } as any);
      toast({
        title: result.devAiEnabled ? "AI enabled for this workspace" : "AI disabled for this workspace",
        description: result.devAiEnabled
          ? `"${wsName}" can now run agents and AI features.`
          : `"${wsName}" AI is off. Other workspaces unaffected.`,
      });
    },
  });

  if (!data?.isDevEnv && !import.meta.env.DEV) return null;
  if (!wsId) return null;

  const enabled = data?.devAiEnabled ?? false;

  if (collapsed) {
    return (
      <button
        data-testid="button-dev-ai-toggle-collapsed"
        onClick={() => toggle.mutate()}
        disabled={toggle.isPending || isLoading}
        title={enabled ? "Dev AI: ON (click to disable)" : "Dev AI: OFF (click to enable)"}
        className={`w-full flex justify-center py-2 rounded-xl transition-all ${
          enabled
            ? "text-amber-400 hover:bg-amber-500/10"
            : "text-muted-foreground/40 hover:text-amber-400 hover:bg-amber-500/[0.06]"
        }`}
      >
        <FlaskConical size={16} className={enabled ? "text-amber-400" : ""} />
      </button>
    );
  }

  return (
    <button
      data-testid="button-dev-ai-toggle"
      onClick={() => toggle.mutate()}
      disabled={toggle.isPending || isLoading}
      className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl border transition-all duration-200 ${
        enabled
          ? "bg-amber-500/10 border-amber-500/30 text-amber-300 hover:bg-amber-500/15"
          : "bg-transparent border-amber-500/15 text-muted-foreground/50 hover:border-amber-500/30 hover:text-amber-400/70 hover:bg-amber-500/[0.04]"
      }`}
    >
      {toggle.isPending ? (
        <Loader2 size={14} className="animate-spin text-amber-400 shrink-0" />
      ) : (
        <FlaskConical size={14} className={`shrink-0 ${enabled ? "text-amber-400" : "text-muted-foreground/40"}`} />
      )}
      <div className="flex-1 text-left min-w-0">
        <span className="text-[11px] font-bold tracking-wide">DEV AI</span>
        <span className={`ml-1.5 text-[9px] font-bold uppercase rounded px-1 py-0.5 ${
          enabled ? "bg-amber-500/20 text-amber-400" : "bg-muted text-muted-foreground/50"
        }`}>
          {enabled ? "ON" : "OFF"}
        </span>
      </div>
      {enabled && <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse shrink-0" />}
    </button>
  );
}

function HeaderCreditsChip() {
  const { data: credits } = useQuery<{ balance: number; allowance: number; creditOverageCap: number | null }>({
    queryKey: ["/api/credits"],
    staleTime: 60000,
    refetchInterval: 120000,
  });
  if (!credits) return null;
  const remaining = Math.max(0, credits.balance);
  const total = credits.allowance;
  const pct = total > 0 ? (remaining / total) * 100 : 0;
  const textColor = pct <= 5 ? "text-rose-400" : pct <= 20 ? "text-amber-400" : "text-emerald-400";
  const barColor = pct <= 5 ? "bg-rose-500" : pct <= 20 ? "bg-amber-500" : "bg-emerald-500";
  const barBg = pct <= 5 ? "bg-rose-500/15" : pct <= 20 ? "bg-amber-500/15" : "bg-emerald-500/15";
  return (
    <a
      href="/settings"
      className="hidden sm:flex flex-col items-start gap-0.5 px-2.5 py-1.5 rounded-lg border border-border hover:bg-muted/30 transition-colors min-w-[90px]"
      data-testid="header-credits-chip"
    >
      <div className="flex items-center gap-1">
        <CreditCard size={10} className={textColor} />
        <span className={`text-[10px] font-semibold ${textColor}`}>{remaining.toLocaleString()} cr.</span>
      </div>
      <div className={`w-full h-1 rounded-full ${barBg}`}>
        <div
          className={`h-1 rounded-full transition-all ${barColor}`}
          style={{ width: `${Math.min(100, pct)}%` }}
          data-testid="header-credits-bar"
        />
      </div>
    </a>
  );
}

function OnboardingOverlay({ onComplete, canDismiss, onDismiss }: { onComplete: () => void; canDismiss: boolean; onDismiss: () => void }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    if (!canDismiss) return;
    function handleEscape(e: KeyboardEvent) {
      if (e.key === "Escape") onDismiss();
    }
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [canDismiss, onDismiss]);

  return (
    <div className="fixed inset-0 z-[200] bg-background overflow-y-auto" data-testid="onboarding-overlay">
      {canDismiss && (
        <button
          data-testid="button-close-onboarding"
          onClick={onDismiss}
          className="absolute top-4 right-4 z-10 w-9 h-9 rounded-xl flex items-center justify-center text-muted-foreground/50 hover:text-muted-foreground hover:bg-muted/50 transition-all"
          aria-label="Close"
        >
          <X size={18} />
        </button>
      )}
      <Onboarding onComplete={onComplete} />
    </div>
  );
}

export default function AppShell({ children }: { children: React.ReactNode }) {
  const [location, navigate] = useLocation();
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const { data: contentItems } = useContent();
  const pendingCount = contentItems?.filter((i: any) => i.approvalStatus === "pending_approval").length || 0;
  const [helpOpen, setHelpOpen] = useState(false);
  const [testerFeedbackOpen, setTesterFeedbackOpen] = useState(false);
  const { theme, setTheme } = useTheme();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("sidebar-collapsed") === "true");
  const [agentsExpanded, setAgentsExpanded] = useState(() => localStorage.getItem("sidebar-agents-expanded") === "true");
  const isDraperPage = location === "/" || location === "/draper";
  const effectiveCollapsed = collapsed;
  const toggleAgentsExpanded = () => {
    const next = !agentsExpanded;
    setAgentsExpanded(next);
    localStorage.setItem("sidebar-agents-expanded", String(next));
  };
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [agentActive, setAgentActive] = useState(false);
  const [activeAgents, setActiveAgents] = useState<Record<string, boolean>>({});
  const agentTimeouts = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const { activeWorkspaceId } = useWorkspaceStore();
  const studioHref = useStudioHref();
  const { data: blogQueueItems = [] } = useQuery<any[]>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "blog-queue"],
    queryFn: async () => {
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/blog-queue`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!activeWorkspaceId,
    staleTime: 30000,
    refetchInterval: 60000,
  });
  const blogQueueCount = blogQueueItems.length;
  const { data: allWorkspaces = [] } = useQuery<Workspace[]>({ queryKey: ["/api/workspaces"] });
  const activeWsName = allWorkspaces.find((w) => w.id === activeWorkspaceId)?.name || "";
  const pendingNudgeRef = useRef(false);

  const { data: scheduleConfig } = useQuery<any>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "schedule-config"],
    enabled: !!activeWorkspaceId,
    queryFn: async () => {
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/schedule-config`, { credentials: "include" });
      if (!res.ok) return null;
      return res.json();
    },
    staleTime: 60000,
  });

  useEffect(() => { setMobileMenuOpen(false); }, [location]);

  useEffect(() => {
    if (!pendingNudgeRef.current || pendingCount === 0) return;
    pendingNudgeRef.current = false;
    toast({
      title: `${pendingCount} post${pendingCount !== 1 ? "s" : ""} awaiting your approval`,
      description: "Peggy created new content — it won't publish until you approve it.",
      duration: 14000,
      action: (
        <ToastAction altText="Review & Approve" onClick={() => navigate(studioHref("review"))}>
          Review & Approve
        </ToastAction>
      ),
    });
  }, [contentItems]);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    const es = new EventSource(`/api/workspaces/${activeWorkspaceId}/stream`, { withCredentials: true } as any);
    let timeout: ReturnType<typeof setTimeout>;
    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === "connected") return;
        setAgentActive(true);
        clearTimeout(timeout);
        timeout = setTimeout(() => setAgentActive(false), 12000);
        const evtAgent = data.agent as string | undefined;
        if (evtAgent && evtAgent !== 'System') {
          setActiveAgents(prev => ({ ...prev, [evtAgent]: true }));
          clearTimeout(agentTimeouts.current[evtAgent]);
          agentTimeouts.current[evtAgent] = setTimeout(() => {
            setActiveAgents(prev => ({ ...prev, [evtAgent]: false }));
          }, 12000);
        }
        if (
          data.agent === "System" &&
          data.status === "completed" &&
          (data.action?.startsWith("Orchestration complete") || data.action?.includes("Orchestration hit max"))
        ) {
          const autoApproveOff = !scheduleConfig?.autoApprovePeggyContent;
          if (autoApproveOff) {
            pendingNudgeRef.current = true;
            queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "content"] });
          }
        }
      } catch {}
    };
    return () => {
      es.close();
      clearTimeout(timeout);
      Object.values(agentTimeouts.current).forEach(clearTimeout);
    };
  }, [activeWorkspaceId, scheduleConfig]);

  function toggleCollapsed() {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem("sidebar-collapsed", String(next));
  }

  const currentThemeIcon = theme === "midnight" ? Moon : theme === "twilight" ? SunMoon : Sun;
  const CurrentThemeIcon = currentThemeIcon;

  return (
    <div className="flex h-screen bg-background text-foreground overflow-hidden">
      <HelpTicketDialog isOpen={helpOpen} onClose={() => setHelpOpen(false)} />
      <TesterFeedbackDialog isOpen={testerFeedbackOpen} onClose={() => setTesterFeedbackOpen(false)} />

      {/* Mobile sidebar overlay */}
      {mobileMenuOpen && (
        <>
          <div className="md:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]" onClick={() => setMobileMenuOpen(false)} />
          <div
            className="md:hidden fixed inset-y-0 left-0 z-[70] w-64 flex flex-col border-r border-border"
            style={{ background: `linear-gradient(to bottom, var(--sidebar-gradient-from), var(--sidebar-gradient-via), var(--sidebar-gradient-to))` }}
          >
            <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(ellipse at top, var(--sidebar-glow), transparent 60%)` }} />
            <div className="relative flex items-center justify-between px-4 py-4 shrink-0">
              <div className="flex items-center gap-2.5">
                <Wordmark fs={17} />
              </div>
              <button onClick={() => setMobileMenuOpen(false)} className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground/50 hover:text-muted-foreground hover:bg-muted/50 transition-all">
                <X size={16} />
              </button>
            </div>
            <div className="relative flex-1 overflow-y-auto hide-scrollbar px-2 pt-1">
              <div className="mb-3 px-2"><span className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground/40 font-bold">War Room</span></div>
              <div className="space-y-0.5">
                {primaryNavItems.map((item) => {
                  const isActive = location === "/" || location === "/draper";
                  return (
                    <Link key={item.to} href={item.to} data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                      className={`relative flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group ${isActive ? "bg-blue-500/[0.08] text-blue-400" : "text-muted-foreground/60 hover:text-foreground/70 hover:bg-card/50"}`}
                    >
                      {isActive && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-4 rounded-r-full bg-blue-500" />}
                      <div className="relative shrink-0">
                        <item.icon size={18} className={isActive ? "text-blue-400" : ""} />
                        {agentActive && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-green-400 animate-pulse" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <span className={`font-semibold text-[13px] block truncate ${isActive ? "text-blue-400" : ""}`}>{item.label}</span>
                        {item.agent && <p className={`text-[9px] font-semibold tracking-wider ${isActive ? "text-blue-400/40" : "text-muted-foreground/35"}`}>{item.agent}</p>}
                      </div>
                    </Link>
                  );
                })}
                <button
                  onClick={toggleAgentsExpanded}
                  data-testid="button-mobile-toggle-agents"
                  className="w-full flex items-center gap-2 px-3 py-1.5 mt-2 rounded-lg text-muted-foreground/35 hover:text-muted-foreground/60 transition-all"
                >
                  <span className="text-[9px] uppercase tracking-[0.2em] font-bold flex-1 text-left">Agents</span>
                  <ChevronRight size={10} className={`transition-transform duration-200 ${agentsExpanded ? "rotate-90" : ""}`} />
                </button>
                {agentsExpanded && agentNavItems.map((item) => {
                  const isActive = location === item.to;
                  return (
                    <Link key={item.to} href={item.to} data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                      className={`relative flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group ${isActive ? "bg-blue-500/[0.08] text-blue-400" : "text-muted-foreground/60 hover:text-foreground/70 hover:bg-card/50"}`}
                    >
                      {isActive && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-4 rounded-r-full bg-blue-500" />}
                      <item.icon size={18} className={isActive ? "text-blue-400" : ""} />
                      <div className="flex items-center gap-1.5 flex-1 min-w-0">
                        <div className="min-w-0">
                          <span className={`font-semibold text-[13px] block truncate ${isActive ? "text-blue-400" : ""}`}>{item.label}</span>
                          {item.agent && <p className={`text-[9px] font-semibold tracking-wider ${isActive ? "text-blue-400/40" : "text-muted-foreground/35"}`}>{item.agent}</p>}
                        </div>
                        {item.to === "/content" && pendingCount > 0 && (
                          <span className="ml-auto bg-yellow-500/20 text-yellow-400 text-[9px] font-bold px-1.5 py-0.5 rounded-full shrink-0" data-testid="badge-pending-count-mobile">
                            {pendingCount}
                          </span>
                        )}
                        {item.to === "/harry" && blogQueueCount > 0 && (
                          <span className="ml-auto bg-violet-500/20 text-violet-400 text-[9px] font-bold px-1.5 py-0.5 rounded-full shrink-0" data-testid="badge-blog-queue-mobile">
                            {blogQueueCount}
                          </span>
                        )}
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
            <div className="relative shrink-0 px-2 pb-4 pt-2">
              <div className="h-px bg-gradient-to-r from-transparent via-foreground/[0.05] to-transparent mb-3 mx-1" />
              <div className="px-2 mb-3">
                <span className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground/40 font-bold block mb-2">Theme</span>
                <div className="flex items-center gap-1 bg-muted/30 rounded-xl p-1" data-testid="mobile-theme-switcher">
                  {themeOptions.map((opt) => {
                    const isActive = theme === opt.value;
                    return (
                      <button
                        key={opt.value}
                        onClick={() => setTheme(opt.value)}
                        data-testid={`button-mobile-theme-${opt.value}`}
                        title={opt.label}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-[10px] font-semibold transition-all duration-200
                          ${isActive ? "bg-blue-500/15 text-blue-400 shadow-sm" : "text-muted-foreground/50 hover:text-muted-foreground hover:bg-muted/50"}`}
                      >
                        <opt.icon size={12} />
                        <span>{opt.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="space-y-0.5">
                <Link href="/guide" className={`flex items-center gap-3 px-3 py-2 rounded-xl transition-all ${location === "/guide" ? "bg-muted text-foreground" : "text-muted-foreground/60 hover:text-muted-foreground hover:bg-card/50"}`} data-testid="mobile-nav-guide">
                  <BookOpen size={18} /><span className="font-semibold text-[12.5px]">Guide</span>
                </Link>
                <button
                  onClick={() => { setMobileMenuOpen(false); setTesterFeedbackOpen(true); }}
                  data-testid="button-tester-feedback-mobile"
                  className="flex items-center gap-3 px-3 py-2 rounded-xl transition-all text-muted-foreground/60 hover:text-muted-foreground hover:bg-card/50 w-full"
                >
                  <ClipboardList size={18} /><span className="font-semibold text-[12.5px]">Tester Feedback</span>
                </button>
                <Link href="/settings" className={`flex items-center gap-3 px-3 py-2 rounded-xl transition-all text-muted-foreground/60 hover:text-muted-foreground hover:bg-card/50`}>
                  <Settings size={18} /><span className="font-semibold text-[12.5px]">Settings</span>
                </Link>
                {user && (
                  <button onClick={() => logout()} className="flex items-center gap-3 px-3 py-2 rounded-xl transition-all text-muted-foreground/50 hover:text-red-400 hover:bg-red-500/[0.06] w-full">
                    <LogOut size={18} /><span className="font-semibold text-[12.5px]">Sign Out</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Desktop Sidebar */}
      <div
        className="hidden md:flex border-r border-border flex-col shrink-0 relative transition-all duration-300 ease-in-out"
        style={{
          width: effectiveCollapsed ? "60px" : "220px",
          background: `linear-gradient(to bottom, var(--sidebar-gradient-from), var(--sidebar-gradient-via), var(--sidebar-gradient-to))`
        }}
      >
        <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(ellipse at top, var(--sidebar-glow), transparent 60%)` }} />

        <div className={`relative flex items-center shrink-0 ${effectiveCollapsed ? "justify-center px-3 py-4" : "justify-between px-4 py-4"}`}>
          {!effectiveCollapsed && (
            <div className="flex items-center gap-2.5">
              <Wordmark fs={17} />
            </div>
          )}

          {effectiveCollapsed ? (
            <div className="flex flex-col items-center gap-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#09090f", border: "1px solid rgba(34,211,238,0.28)", boxShadow: "0 0 10px rgba(34,211,238,0.15)" }}>
                <span className="w-3 h-3 rounded-full" style={{ background: "#22d3ee", boxShadow: "0 0 6px rgba(34,211,238,0.5)" }} />
              </div>
              <button
                onClick={toggleCollapsed}
                data-testid="button-sidebar-expand"
                title="Expand sidebar"
                className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground/40 hover:text-muted-foreground hover:bg-muted/50 transition-all"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          ) : (
            <button
              onClick={toggleCollapsed}
              data-testid="button-sidebar-collapse"
              title="Collapse sidebar"
              className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground/40 hover:text-muted-foreground hover:bg-muted/50 transition-all ml-auto"
            >
              <ChevronLeft size={15} />
            </button>
          )}
        </div>

        <div className="relative flex-1 overflow-y-auto min-h-0 hide-scrollbar px-2 pt-1">
          {!effectiveCollapsed && (
            <div className="mb-3 px-2">
              <span className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground/40 font-bold">War Room</span>
            </div>
          )}

          <div className="space-y-0.5">
            {/* Primary nav — Command always visible */}
            {primaryNavItems.map((item) => {
              const isActive = location === "/" || location === "/draper";
              return (
                <Link
                  key={item.to}
                  href={item.to}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                  title={effectiveCollapsed ? item.label : undefined}
                  className={`relative flex items-center rounded-xl transition-all duration-200 group
                    ${effectiveCollapsed ? "justify-center px-0 py-2.5 mx-0" : "gap-3 px-3 py-2"}
                    ${isActive ? "bg-blue-500/[0.08] text-blue-400" : "text-muted-foreground/60 hover:text-foreground/70 hover:bg-card/50"}
                  `}
                >
                  {isActive && !effectiveCollapsed && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-4 rounded-r-full bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.4)]" />}
                  <div className="relative shrink-0">
                    <item.icon size={18} className={`transition-colors duration-200 ${isActive ? "text-blue-400" : "group-hover:text-muted-foreground"}`} />
                    {agentActive && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-green-400 animate-pulse" title="Agents are running" />}
                  </div>
                  {!effectiveCollapsed && (
                    <div className="min-w-0 flex-1">
                      <span className={`font-semibold text-[12.5px] tracking-wide leading-tight block truncate ${isActive ? "text-blue-400" : ""}`}>{item.label}</span>
                      {item.agent && <p className={`text-[9px] font-semibold tracking-wider ${isActive ? "text-blue-400/40" : "text-muted-foreground/35"}`}>{item.agent}</p>}
                    </div>
                  )}
                </Link>
              );
            })}

            {/* Agents section — collapsible */}
            {!effectiveCollapsed && (
              <button
                onClick={toggleAgentsExpanded}
                data-testid="button-toggle-agents-section"
                className="w-full flex items-center gap-2 px-3 py-1.5 mt-2 rounded-lg text-muted-foreground/35 hover:text-muted-foreground/60 transition-all group"
              >
                <span className="text-[9px] uppercase tracking-[0.2em] font-bold flex-1 text-left">Agents</span>
                <ChevronRight size={10} className={`transition-transform duration-200 ${agentsExpanded ? "rotate-90" : ""}`} />
              </button>
            )}
            {effectiveCollapsed && (
              <button
                onClick={toggleAgentsExpanded}
                title="Agents"
                data-testid="button-toggle-agents-section-collapsed"
                className="w-full flex justify-center py-2 rounded-xl text-muted-foreground/30 hover:text-muted-foreground/60 transition-all mt-1"
              >
                <ChevronRight size={12} className={`transition-transform duration-200 ${agentsExpanded ? "rotate-90" : ""}`} />
              </button>
            )}
            {agentsExpanded && agentNavItems.map((item) => {
              const isActive = location.startsWith(item.to);
              return (
                <Link
                  key={item.to}
                  href={item.to}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                  title={effectiveCollapsed ? item.label : undefined}
                  className={`relative flex items-center rounded-xl transition-all duration-200 group
                    ${effectiveCollapsed ? "justify-center px-0 py-2.5 mx-0" : "gap-3 px-3 py-2"}
                    ${isActive ? "bg-blue-500/[0.08] text-blue-400" : "text-muted-foreground/60 hover:text-foreground/70 hover:bg-card/50"}
                  `}
                >
                  {isActive && !effectiveCollapsed && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-4 rounded-r-full bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.4)]" />}
                  <div className="relative shrink-0">
                    <item.icon size={18} className={`transition-colors duration-200 ${isActive ? "text-blue-400" : "group-hover:text-muted-foreground"}`} />
                    {effectiveCollapsed && pendingCount > 0 && item.to === "/content" && (
                      <span className="absolute -top-1 -right-1 min-w-[14px] h-3.5 bg-yellow-500 text-[7px] font-bold rounded-full flex items-center justify-center text-black px-0.5" data-testid="badge-pending-count-collapsed">
                        {pendingCount > 9 ? "9+" : pendingCount}
                      </span>
                    )}
                    {effectiveCollapsed && blogQueueCount > 0 && item.to === "/harry" && (
                      <span className="absolute -top-1 -right-1 min-w-[14px] h-3.5 bg-violet-500 text-[7px] font-bold rounded-full flex items-center justify-center text-white px-0.5" data-testid="badge-blog-queue-collapsed">
                        {blogQueueCount > 9 ? "9+" : blogQueueCount}
                      </span>
                    )}
                  </div>
                  {!effectiveCollapsed && (
                    <div className="flex items-center gap-1.5 flex-1 min-w-0">
                      <div className="min-w-0">
                        <span className={`font-semibold text-[12.5px] tracking-wide leading-tight block truncate ${isActive ? "text-blue-400" : ""}`}>{item.label}</span>
                        {item.agent && <p className={`text-[9px] font-semibold tracking-wider ${isActive ? "text-blue-400/40" : "text-muted-foreground/35"}`}>{item.agent}</p>}
                      </div>
                      {item.to === "/content" && pendingCount > 0 && (
                        <span className="ml-auto bg-yellow-500/20 text-yellow-400 text-[9px] font-bold px-1.5 py-0.5 rounded-full shrink-0" data-testid="badge-pending-count">
                          {pendingCount}
                        </span>
                      )}
                      {item.to === "/harry" && blogQueueCount > 0 && (
                        <span className="ml-auto bg-violet-500/20 text-violet-400 text-[9px] font-bold px-1.5 py-0.5 rounded-full shrink-0" data-testid="badge-blog-queue">
                          {blogQueueCount}
                        </span>
                      )}
                    </div>
                  )}
                </Link>
              );
            })}
          </div>
        </div>

        {/* Pinned footer — always visible, never scrolls away */}
        <div className="relative shrink-0 px-2 pb-3 pt-2">
          <div className="h-px bg-gradient-to-r from-transparent via-foreground/[0.05] to-transparent mb-3 mx-1" />
          {effectiveCollapsed ? (
            <div className="flex flex-col items-center gap-0.5">
              {/* Agent status pulse dots */}
              <div className="flex flex-col items-center gap-1.5 py-2 mb-1" data-testid="agent-pulse-dots">
                {[
                  { name: "Draper", color: "#60a5fa" },
                  { name: "Peggy", color: "#f472b6" },
                  { name: "Pete", color: "#fbbf24" },
                  { name: "Rogers", color: "#34d399" },
                  { name: "Harry", color: "#a78bfa" },
                ].map(agent => (
                  <div
                    key={agent.name}
                    title={activeAgents[agent.name] ? `${agent.name} — running` : agent.name}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      activeAgents[agent.name]
                        ? 'opacity-100 animate-pulse scale-110'
                        : 'opacity-30 hover:opacity-60'
                    }`}
                    style={{ background: agent.color }}
                    data-testid={`dot-agent-${agent.name.toLowerCase()}`}
                  />
                ))}
              </div>
              <div className="h-px w-8 mx-auto mb-1" style={{ background: "rgba(255,255,255,0.06)" }} />
              <button
                onClick={() => {
                  const next = theme === "midnight" ? "twilight" : theme === "twilight" ? "daylight" : "midnight";
                  setTheme(next);
                }}
                data-testid="button-theme-cycle"
                title={`Theme: ${theme}`}
                className="w-full flex justify-center py-2 rounded-xl text-muted-foreground/50 hover:text-muted-foreground hover:bg-muted/50 transition-all"
              >
                <CurrentThemeIcon size={16} />
              </button>
              <Link
                href="/settings"
                data-testid="nav-settings"
                title="Settings"
                className={`w-full flex justify-center py-2 rounded-xl transition-all
                  ${location === "/settings" ? "text-foreground bg-muted/50" : "text-muted-foreground/50 hover:text-muted-foreground hover:bg-card/50"}
                `}
              >
                <Settings size={18} />
              </Link>
              {user && (
                <button
                  data-testid="button-logout"
                  onClick={() => logout()}
                  title="Sign Out"
                  className="w-full flex justify-center py-2 rounded-xl text-muted-foreground/40 hover:text-red-400 hover:bg-red-500/[0.06] transition-all"
                >
                  <LogOut size={18} />
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="flex items-center gap-1 mb-2 bg-muted/30 rounded-xl p-1" data-testid="theme-switcher">
                {themeOptions.map((opt) => {
                  const isActive = theme === opt.value;
                  return (
                    <button
                      key={opt.value}
                      onClick={() => setTheme(opt.value)}
                      data-testid={`button-theme-${opt.value}`}
                      title={opt.label}
                      className={`flex-1 flex items-center justify-center py-1.5 rounded-lg text-[10px] font-semibold transition-all duration-200
                        ${isActive
                          ? "bg-blue-500/15 text-blue-400 shadow-sm"
                          : "text-muted-foreground/50 hover:text-muted-foreground hover:bg-muted/50"
                        }
                      `}
                    >
                      <opt.icon size={13} />
                    </button>
                  );
                })}
              </div>
              <div className="space-y-0.5">
                <Link
                  href="/settings"
                  data-testid="nav-settings"
                  className={`relative flex items-center gap-3 px-3 py-2 rounded-xl transition-all duration-200 group
                    ${location === "/settings"
                      ? "bg-muted text-foreground"
                      : "text-muted-foreground/60 hover:text-muted-foreground hover:bg-card/50"
                    }
                  `}
                >
                  <Settings size={18} />
                  <span className="font-semibold text-[12.5px]">Settings</span>
                </Link>
                {user && (
                  <button
                    data-testid="button-logout"
                    onClick={() => logout()}
                    className="flex items-center gap-3 px-3 py-2 rounded-xl text-muted-foreground/50 hover:text-red-400 hover:bg-red-500/[0.06] transition-all duration-200 w-full"
                  >
                    <LogOut size={18} />
                    <span className="font-semibold text-[12.5px]">Sign Out</span>
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {showOnboarding && (
          <OnboardingOverlay
            onComplete={() => { setShowOnboarding(false); queryClient.invalidateQueries({ queryKey: ["/api/workspaces"] }); }}
            canDismiss={allWorkspaces.length > 0}
            onDismiss={() => setShowOnboarding(false)}
          />
        )}
        <header className="h-14 border-b border-border flex items-center justify-between gap-4 px-4 md:px-5 bg-background/50 backdrop-blur-2xl shrink-0 z-50">
          <div className="flex items-center gap-3 min-w-0">
            <button
              data-testid="button-mobile-menu"
              onClick={() => setMobileMenuOpen(true)}
              className="md:hidden w-9 h-9 rounded-xl flex items-center justify-center text-muted-foreground/70 hover:text-foreground hover:bg-muted/50 transition-all shrink-0"
              aria-label="Open menu"
            >
              <Menu size={20} />
            </button>
            <WorkspaceSwitcher onRequestOnboarding={() => setShowOnboarding(true)} />
          </div>
          {user && (
            <div className="flex items-center gap-3 shrink-0">
              <HeaderCreditsChip />
              <span className="text-[13px] text-muted-foreground hidden sm:block font-medium" data-testid="text-username">
                {user.firstName || user.email}
              </span>
              {user.profileImageUrl ? (
                <img src={user.profileImageUrl} alt="" className="w-8 h-8 rounded-full ring-2 ring-foreground/[0.08] ring-offset-2 ring-offset-background" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500/30 to-purple-500/30 border border-border flex items-center justify-center">
                  <span className="text-[11px] font-bold text-muted-foreground">
                    {(user.firstName || user.email || "U").charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
            </div>
          )}
        </header>
        <main className={`flex-1 min-h-0 ${
          location === "/draper" || location === "/"
            ? "overflow-hidden p-0"
            : "overflow-y-auto p-3 md:p-6 custom-scrollbar pb-20 md:pb-6"
        }`}>
          {children}
        </main>

        {/* Mobile bottom navigation */}
        <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-xl border-t border-border flex items-center justify-around px-2 py-2 safe-area-pb" data-testid="mobile-bottom-nav">
          {navItems.map((item) => {
            const isActive = location === item.to;
            return (
              <Link
                key={item.to}
                href={item.to}
                data-testid={`mobile-bottom-nav-${item.label.toLowerCase()}`}
                className={`flex flex-col items-center gap-1 px-4 py-2 rounded-xl transition-all ${isActive ? "text-blue-400" : "text-muted-foreground/50"}`}
              >
                <div className="relative">
                  <item.icon size={20} />
                  {item.to === "/content" && pendingCount > 0 && (
                    <span className="absolute -top-1 -right-1.5 min-w-[14px] h-3.5 bg-yellow-500 text-[7px] font-bold rounded-full flex items-center justify-center text-black px-0.5" data-testid="badge-pending-count-bottom-nav">
                      {pendingCount > 9 ? "9+" : pendingCount}
                    </span>
                  )}
                  {item.to === "/harry" && blogQueueCount > 0 && (
                    <span className="absolute -top-1 -right-1.5 min-w-[14px] h-3.5 bg-violet-500 text-[7px] font-bold rounded-full flex items-center justify-center text-white px-0.5" data-testid="badge-blog-queue-bottom-nav">
                      {blogQueueCount > 9 ? "9+" : blogQueueCount}
                    </span>
                  )}
                </div>
                <span className="text-[10px] font-bold tracking-wide">{item.label}</span>
              </Link>
            );
          })}
          <Link
            href="/settings"
            data-testid="mobile-bottom-nav-settings"
            className={`flex flex-col items-center gap-1 px-4 py-2 rounded-xl transition-all ${location === "/settings" ? "text-blue-400" : "text-muted-foreground/50"}`}
          >
            <Settings size={20} />
            <span className="text-[10px] font-bold tracking-wide">Settings</span>
          </Link>
        </nav>
      </div>
      <ReportWidget />
    </div>
  );
}
